// DOC-DEPS: LLM.md -> docs/ACTIVITY_LEXICON.md -> docs/ACTIVITY_MOOD_AUTO_RECOGNITION.md
//
// Chinese mood lexicon – single source of truth for all ZH mood signals.
//
// Previously these entries were scattered across three files:
//   • src/services/input/liveInputRules.zh.ts  (ZH_MOOD_WORDS, ZH_MOOD_KEYWORDS, ZH_MOOD_PATTERNS)
//   • src/lib/mood.ts                           (explicitMoodDict, dict)
//   • src/services/input/magicPenTodoSalvage.ts (ZH_MOOD_ONLY_PATTERN)
//
// All consumers should import from this file (directly or via getLexicon.ts).

import type { MoodLexicon } from './types.js';

export const zhMoodLexicon: MoodLexicon = {

  // ── 显式心情词 → MoodKey 映射 ────────────────────────────────────────────
  // 直接表达情绪状态的词；命中则立即返回，优先级最高。
  explicitMoodMap: [
    { pattern: /(开心|高兴|愉快|兴奋|快乐|爽|美滋滋|喜滋滋|雀跃|痛快)/, mood: 'happy' },
    { pattern: /(平静|放松|舒缓|安心|松弛|淡定|惬意|舒服|轻松|安稳|平和|释然)/, mood: 'calm' },
    { pattern: /(专注|投入|高效|在状态|沉浸|专心|心流|全神贯注)/, mood: 'focused' },
    {
      pattern: /(满足|满意|踏实|有成就|充实|欣慰|感恩|庆幸|成就感|解脱|如释重负|顺利|松口气|松了口气|松一口气|缓口气)/,
      mood: 'satisfied',
    },
    { pattern: /(累|疲惫|困|倦|没精神|精力不够|头疼|头晕|胃疼|头秃|想睡觉|犯困|乏力|昏沉|筋疲力尽|心累)/, mood: 'tired' },
    { pattern: /(焦虑|紧张|担心|不安|害怕|恐惧|心虚|慌|焦灼|忐忑|发慌)/, mood: 'anxious' },
    { pattern: /(无聊|没劲|没意思|空虚)/, mood: 'bored' },
    {
      pattern: /(低落|难受|难过|伤心|沮丧|烦|崩溃|痛苦|郁闷|委屈|糟糕|压抑|窝火|憋屈|心酸|心疼|冤枉|挫败|失望|状态差|不满意|烦躁|破防|无助|绝望|麻木|窒息)/,
      mood: 'down',
    },
  ],

  // ── 活动内容 → 推断心情映射 ──────────────────────────────────────────────
  // 当输入不含显式心情词时，根据活动关键词推断可能的心情。
  activityMoodMap: [
    // happy: 运动、饮食、聚会、社交、娱乐
    {
      pattern: /(跑步|运动|健身|瑜伽|舞|游泳|撸铁|拉伸|跳绳|跳操|打球|羽毛球|篮球|足球|网球|乒乓球|徒步|爬山|登山|骑行|露营)/,
      mood: 'happy',
    },
    {
      pattern: /(吃饭|用餐|午饭|午餐|晚饭|晚餐|早饭|早餐|夜宵|聚餐|外卖|点外卖|做饭|野餐|约饭|干饭)/,
      mood: 'happy',
    },
    {
      pattern: /(聊天|闲聊|唠嗑|连麦|面基|约会|见朋友|聚会|逛街|探店|看展|看演出|看电影|k歌|唱歌)/i,
      mood: 'happy',
    },
    // satisfied: 完成任务
    { pattern: /(完成|提交|通过|达成|搞定|收尾|交付|上线|汇报通过)/, mood: 'satisfied' },
    // focused: 深度工作 / 学习
    {
      pattern: /(学习|上课|看书|阅读|写作业|写论文|备考|复习|写代码|开发|办公|工作|上班|写周报|做PPT|写测试|写文档|写总结|竞品分析|review|开会|写方案|做汇报)/i,
      mood: 'focused',
    },
    // tired: 加班、通勤、熬夜
    { pattern: /(加班|熬夜|休息不够|打瞌睡|赶路|通勤|赶地铁)/, mood: 'tired' },
    // bored: 等待、无事可做
    { pattern: /(排队|等候|堵车|发呆|无所事事|刷手机|看手机)/, mood: 'bored' },
    // down: 失败、被骂
    { pattern: /(失误|失败|延期|卡住|崩溃|故障|被老板骂|论文改.*崩溃)/, mood: 'down' },
    // calm: 休闲放松
    {
      pattern: /(冥想|散心|喝茶|咖啡|散步|泡澡|泡脚|遛弯|听歌|听播客|看小说|看动漫|追剧|追番|刷短视频|看直播)/,
      mood: 'calm',
    },
  ],

  // ── 所有心情词（用于"输入中是否含有心情信号"的快速检测）──────────────────
  allMoodWords: [
    // 正面
    '开心', '高兴', '愉快', '兴奋', '快乐', '爽', '美滋滋', '喜滋滋', '雀跃', '痛快',
    // 平静
    '平静', '放松', '舒缓', '安心', '松弛', '淡定', '惬意', '舒服', '轻松', '安稳', '平和', '释然',
    // 专注
    '专注', '投入', '高效', '在状态', '沉浸', '专心', '心流', '全神贯注',
    // 满足
    '满足', '满意', '踏实', '充实', '欣慰', '感恩', '庆幸', '成就感', '解脱', '如释重负', '松口气', '松一口气',
    // 疲惫
    '累', '疲惫', '困', '倦', '没精神', '头疼', '头晕', '胃疼', '头秃', '想睡觉', '犯困', '乏力', '昏沉', '筋疲力尽', '心累',
    // 焦虑
    '焦虑', '紧张', '不安', '担心', '害怕', '恐惧', '心虚', '焦灼', '忐忑', '发慌',
    // 无聊
    '无聊', '没劲', '没意思', '空虚',
    // 低落
    '低落', '难受', '难过', '伤心', '沮丧', '烦', '崩溃', '痛苦', '糟糕', '压抑', '郁闷',
    '窝火', '憋屈', '心酸', '心疼', '委屈', '冤枉', '烦躁', '破防', '无助', '绝望', '麻木', '窒息',
    // 网络热词
    '人麻了', '裂开', '绷不住', 'emo', '上头', '心态炸了', '内耗', '摆烂', '躺平', '蚌埠住了', '破大防',
    '无敌', '服了', '醉了', '晕了', '受不了', '算了', '随便',
    // 其他情感词
    '迷茫', '期待', '激动', '感动', '孤独', '寂寞', '尴尬', '羡慕', '嫉妒', '后悔',
    '状态差', '顺利', '不满意', '失望', '挫败',
    // 身体感受（可作为心情信号）
    '饿', '渴',
  ],

  // ── 心情句式正则 ────────────────────────────────────────────────────────────
  // 全句结构性模式，匹配"好累"、"心情很好"、"有点焦虑"等构式。
  moodSentencePatterns: [
    /^好(累|烦|焦虑|难受|难过|开心|崩溃|痛苦|疲惫|困|饿|委屈|郁闷|紧张)/,
    /^很(累|烦|焦虑|难受|难过|开心|崩溃|痛苦|疲惫|困|饿|委屈|郁闷|紧张)/,
    /^有点(累|烦|焦虑|难受|难过|乱|困|饿|紧张|崩溃|低落)/,
    /^今天状态.+/,
    /^真(烦|累|难受|难过|焦虑|崩溃|无语|糟糕|痛苦|开心)/,
    /心里(很)?(乱|堵|慌|难受|不舒服|发毛)/,
    /(有点|有些|有一点)(烦躁|焦灼|忐忑|emo|破防)/i,
    /(提不起劲|不在状态|没状态|没啥状态)/,
    /心情(好好|很好|挺好|还行|一般|不太好|很差|不好|低落|不错|糟糕|崩溃)/,
    /状态(好好|很好|挺好|还行|一般|不太好|很差|不好|低落|不错|糟糕|崩溃)/,
    /感觉(不错|很好|轻松|糟糕)/,
    /[得地]很(好|顺利)/,
  ],
};
